import { AxiosError } from 'axios';

export interface ApiError {
  message: string;
  status?: number;
  detail?: string;
}

// Type guard for Axios errors
export function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}

export const handleApiError = (error: unknown): ApiError => {
  // Handle Axios errors
  if (isAxiosError(error)) {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data as any;
      return {
        message: data?.detail || data?.message || 'An error occurred',
        status: error.response.status,
        detail: data?.detail
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'No response from server. Please check your connection.',
        status: 0
      };
    }
  }
  
  // Handle regular Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      status: 0
    };
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      status: 0
    };
  }
  
  // Handle unknown errors
  return {
    message: 'An unexpected error occurred',
    status: 0
  };
};

export const getErrorMessage = (error: unknown): string => {
  const apiError = handleApiError(error);
  return apiError.message;
};

export const isAuthError = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    return error.response?.status === 401;
  }
  return false;
};

export const isNotFoundError = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    return error.response?.status === 404;
  }
  return false;
};

export const isValidationError = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    return error.response?.status === 422;
  }
  return false;
};